import React, { useState, useEffect} from 'react';
import { useSelector, useDispatch } from 'react-redux';
import {
  Button,
  Divider,
  FormControlLabel,
  FormHelperText,
  Grid,
  Icon,
  LinearProgress,
  Radio,
  RadioGroup,
  TextField,
  Typography,
  Snackbar
} from '@material-ui/core';
import Autocomplete from '@material-ui/lab/Autocomplete';
import MuiAlert from '@material-ui/lab/Alert';
import parse from 'autosuggest-highlight/parse';
import match from 'autosuggest-highlight/match';
import {
  uploadPrivateImageApi,
  getAssetManagerImagesApi
} from 'api';
import {
  changeHomepageCTABlockConfig,
  changeHomepageCTACategory
} from 'redux/actions/store_configs';
import {
  uploadFileTypeUnsupported,
  uploadFileSizeLimit,
  uploadImageResolutionLimit
} from 'redux/actions/general';
import { ImageGalleryDialog } from 'components';
import { assetManagerUrl } from 'configs';


const MAX_CTA_FILE_SIZE = 250 * 1024;

const CTAVertical = ({values, errors, setFieldValue}) => {
  const dispatch = useDispatch();
  const { vertical } = values;
  const [openImageDialog, toggleOpenImageDialog] = useState(false);
  const [openOwnImageDialog, toggleOpenOwnImageDialog] = useState(false);
  const [ownImagesTagIds, setOwnImagesTagIds] = useState('');

  const {
    storeActiveCategoriesWithActiveNavLevel,
    loadingCategories,
    errorCategories
  } = useSelector((state) => state.categories);

  const {
    assetCategoryTags,
    assetImagesData,
    currentCategoryId
  } = useSelector((state) => state.asset_manager);

  const {profile: {stores = []}} = useSelector((state) => state.user);

  const storeCode = stores.length && stores[0]?.storeCode;

  const verticalCTATagData = assetCategoryTags.find((tag) => tag.tagCode === 'block_1_body_desktop');
  const privateTagData = assetCategoryTags.find((tag) => tag.tagCode === 'private');
  const storeCodeTagData = assetCategoryTags.find((tag) => tag.tagCode === `${storeCode}`);

  useEffect(() => {
    if (verticalCTATagData && privateTagData && storeCodeTagData) {
      setOwnImagesTagIds(`${verticalCTATagData.id}, ${privateTagData.id}, ${storeCodeTagData.id}`);
    }
  }, [verticalCTATagData, privateTagData, storeCodeTagData]);

  useEffect(() => {
    getAssetManagerImagesApi({
      dispatch,
      limit: 20,
      page: 1,
      tagIds: ownImagesTagIds,
      isPrivate: true
    });
  }, [ownImagesTagIds]);

  const handleChange = (event) => {
    const {name, checked, type, value} = event.target;
    dispatch(changeHomepageCTABlockConfig(name, checked, type, value, 'vertical'));
    setFieldValue(`vertical[${name}]`, value);
  };

  const handleSelectCategory = (value, actionType, configTab, configPath) => {
    dispatch(changeHomepageCTACategory(actionType, value, configTab, configPath));
    setFieldValue(`vertical[${configPath}]`, value ? value.id : '');
  };

  const handleSelectImage = (data) => {
    const value = `${assetManagerUrl}/${data.webPath}`;
    dispatch(changeHomepageCTABlockConfig(
      'dermpro/design/cta_image_1_desktop',
      null,
      'text',
      value,
      'vertical'
    ));
    dispatch(changeHomepageCTABlockConfig(
      'dermpro/design/cta_image_1_mobile',
      null,
      'text',
      value,
      'vertical'
    ));
    setFieldValue('vertical[\'dermpro/design/cta_image_1_desktop\']', value);
    setFieldValue('vertical[\'dermpro/design/cta_image_1_mobile\']', value);
  };

  const handleUpLoadImage = async (event) => {
    const {files, value} = event.target;
    const formData = new FormData();

    if (files.length) {
      const fileTypeDotIndexPosition = value.lastIndexOf('.') + 1;
      const slicedFileTypeFromFilePath = value.slice(fileTypeDotIndexPosition);
      const acceptableFormats = 'png|jpeg';
      const hasImageFile = new RegExp(`(image\\/(${acceptableFormats}))`, 'gi');

      const reader = new FileReader();

      reader.readAsDataURL(files[0]);
      reader.onload = (e) => {
        const image = new Image();

        image.src = e.target.result;
        image.onload = async () => {
          const {height, width} = image;

          if (!hasImageFile.test(files[0].type) && !hasImageFile.test(slicedFileTypeFromFilePath)) {
            dispatch(uploadFileTypeUnsupported());
          } else if (width !== 380 || height !== 552) {
            dispatch(uploadImageResolutionLimit('380 x 552px'));
          } else if (files[0].size > MAX_CTA_FILE_SIZE) {
            dispatch(uploadFileSizeLimit('250kB'));
          } else {
            formData.append('image', files[0]);

            if (ownImagesTagIds.length) {
              formData.append('tags', `block_1_body_desktop,private,${storeCode}`);
            } else {
              const tags = [
                verticalCTATagData.tagCode,
                privateTagData.tagCode,
                {
                  label: storeCode,
                  tagCode: storeCode,
                  category: currentCategoryId
                }
              ];
              formData.append('tags', JSON.stringify(tags));
            }

            const uploadedData = await uploadPrivateImageApi({
              dispatch,
              formData,
              isAddingNewTag: !ownImagesTagIds.length,
              categoryName: 'Storebuild-automaton'
            });

            if (uploadedData) {
              handleSelectImage(uploadedData);
            }
          }
        };
      };
    }
  };

  const isCustomLinkTitleError1 = errors?.vertical?.['store_info/cta/cta_vertical_mask_custom_link_title_1'];
  const isCustomLinkError1 = errors?.vertical?.['store_info/cta/cta_vertical_mask_custom_link_1'];
  const isCustomLinkTitleError2 = errors?.vertical?.['store_info/cta/cta_vertical_mask_custom_link_title_2'];
  const isCustomLinkError2 = errors?.vertical?.['store_info/cta/cta_vertical_mask_custom_link_2'];
  const isCustomLinkTitleError3 = errors?.vertical?.['store_info/cta/cta_vertical_mask_custom_link_title_3'];
  const isCustomLinkError3 = errors?.vertical?.['store_info/cta/cta_vertical_mask_custom_link_3'];
  const isCustomLinkTitleError4 = errors?.vertical?.['store_info/cta/cta_vertical_mask_custom_link_title_4'];
  const isCustomLinkError4 = errors?.vertical?.['store_info/cta/cta_vertical_mask_custom_link_4'];
  const isCustomLinkTitleError5 = errors?.vertical?.['store_info/cta/cta_vertical_mask_custom_link_title_5'];
  const isCustomLinkError5 = errors?.vertical?.['store_info/cta/cta_vertical_mask_custom_link_5'];
  const isCustomLinkTitleError6 = errors?.vertical?.['store_info/cta/cta_vertical_mask_custom_link_title_6'];
  const isCustomLinkError6 = errors?.vertical?.['store_info/cta/cta_vertical_mask_custom_link_6'];


  return (
    <div>
      <Typography variant="h4" className="my-6 mx-3">CTA Vertical Block</Typography>
      <Divider className="mb-8" />
      <Grid container spacing={3} className="mb-4">
        <Grid item xs={12} sm={2} className="my-2 ml-4">Block Title</Grid>
        <Grid item xs={12} sm={9}>
          <TextField
            fullWidth
            variant="outlined"
            name={'store_info/cta/cta_vertical_title'}
            label="Text"
            value={vertical['store_info/cta/cta_vertical_title']}
            onChange={handleChange}
            inputProps={{
              shrink: true
            }}
          />
        </Grid>
        <Grid item xs={12} sm={2} className="my-2 ml-4">Block Mask Title</Grid>
        <Grid item xs={12} sm={9}>
          <TextField
            fullWidth
            variant="outlined"
            name={'store_info/cta/cta_vertical_mask_title'}
            label="Text"
            value={vertical['store_info/cta/cta_vertical_mask_title']}
            onChange={handleChange}
            inputProps={{
              shrink: true
            }}
          />
        </Grid>
        <Grid item xs={12} sm={2} className="my-2 ml-4">Link 1</Grid>
        <Grid item xs={12} sm={9}>
          <RadioGroup
            row
            aria-label={'select option 1'}
            name={'store_info/cta/is_cta_vertical_mask_category_1'}
            value={vertical['store_info/cta/is_cta_vertical_mask_category_1']}
            onChange={handleChange}
            className="mb-2"
          >
            <FormControlLabel
              value="1"
              control={<Radio size="small" color="primary" />}
              label="Category"
            />
            <FormControlLabel
              value="0"
              control={<Radio size="small" color="primary" />}
              label="Custom"
            />
          </RadioGroup>
          {vertical['store_info/cta/is_cta_vertical_mask_category_1'] == 1 ?
            <>
              {loadingCategories && <LinearProgress className="mt-4" />}
              {!loadingCategories && errorCategories && <div>Can&apos;t fetch categories data</div>}
              {!loadingCategories && !errorCategories && storeActiveCategoriesWithActiveNavLevel.length &&
              <Autocomplete
                size="small"
                fullWidth
                options={storeActiveCategoriesWithActiveNavLevel}
                getOptionLabel={(option) => option.nameWithPath || ''}
                value={storeActiveCategoriesWithActiveNavLevel.find((category) => (
                  category.id == vertical['store_info/cta/cta_vertical_mask_categoryId_1']) || null
                )}
                getOptionSelected={(option) => (
                  vertical['store_info/cta/cta_vertical_mask_categoryId_1'] == option.id
                )}
                onChange={(_, value, action) => (
                  handleSelectCategory(
                    value,
                    action,
                    'vertical',
                    'store_info/cta/cta_vertical_mask_categoryId_1'
                  )
                )}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    variant="outlined"
                    label="Category"
                  />
                )}
                renderOption={(option, { inputValue }) => {
                  const matches = match(option.nameWithPath, inputValue);
                  const parts = parse(option.nameWithPath, matches);
                  return (
                    <div>
                      {parts.map((part, index) => (
                        <span key={index} className={`${part.highlight ? 'text-primary font-bold' : ''}`}>
                          {part.text}
                        </span>
                      ))}
                    </div>
                  );
                }}
              />
              }
            </>
            :
            <>
              <TextField
                fullWidth
                variant="outlined"
                name={'store_info/cta/cta_vertical_mask_custom_link_title_1'}
                label="Title"
                value={
                  vertical['store_info/cta/cta_vertical_mask_custom_link_title_1'] !== null
                    ? vertical['store_info/cta/cta_vertical_mask_custom_link_title_1']
                    : ''
                }
                onChange={handleChange}
                className="mb-5"
                inputProps={{
                  shrink: 'true'
                }}
                error={isCustomLinkTitleError1}
                helperText={
                  isCustomLinkTitleError1 &&
                  errors.vertical['store_info/cta/cta_vertical_mask_custom_link_title_1']
                }
              />
              <TextField
                fullWidth
                variant="outlined"
                name={'store_info/cta/cta_vertical_mask_custom_link_1'}
                label="Link"
                value={
                  vertical['store_info/cta/cta_vertical_mask_custom_link_1'] !== null
                    ? vertical['store_info/cta/cta_vertical_mask_custom_link_1']
                    : ''
                }
                onChange={handleChange}
                inputProps={{
                  shrink: 'true'
                }}
                error={isCustomLinkError1}
                helperText={
                  isCustomLinkError1 &&
                  errors.vertical['store_info/cta/cta_vertical_mask_custom_link_1']
                }
              />
            </>
          }
        </Grid>
        <Grid item xs={12} sm={2} className="my-2 ml-4">Link 2</Grid>
        <Grid item xs={12} sm={9}>
          <RadioGroup
            row
            aria-label={'select option 2'}
            name={'store_info/cta/is_cta_vertical_mask_category_2'}
            value={vertical['store_info/cta/is_cta_vertical_mask_category_2']}
            onChange={handleChange}
            className="mb-2"
          >
            <FormControlLabel
              value="1"
              control={<Radio size="small" color="primary" />}
              label="Category"
            />
            <FormControlLabel
              value="0"
              control={<Radio size="small" color="primary" />}
              label="Custom"
            />
          </RadioGroup>
          {vertical['store_info/cta/is_cta_vertical_mask_category_2'] == 1 ?
            <>
              {loadingCategories && <LinearProgress className="mt-4" />}
              {!loadingCategories && errorCategories && <div>Can&apos;t fetch categories data</div>}
              {!loadingCategories && !errorCategories && storeActiveCategoriesWithActiveNavLevel.length &&
              <Autocomplete
                size="small"
                fullWidth
                options={storeActiveCategoriesWithActiveNavLevel}
                getOptionLabel={(option) => option.nameWithPath || ''}
                value={storeActiveCategoriesWithActiveNavLevel.find((category) => (
                  category.id == vertical['store_info/cta/cta_vertical_mask_categoryId_2']) || null
                )}
                getOptionSelected={(option) => (
                  vertical['store_info/cta/cta_vertical_mask_categoryId_2'] == option.id
                )}
                onChange={(_, value, action) => (
                  handleSelectCategory(
                    value,
                    action,
                    'vertical',
                    'store_info/cta/cta_vertical_mask_categoryId_2'
                  )
                )}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    variant="outlined"
                    label="Category"
                  />
                )}
                renderOption={(option, { inputValue }) => {
                  const matches = match(option.nameWithPath, inputValue);
                  const parts = parse(option.nameWithPath, matches);
                  return (
                    <div>
                      {parts.map((part, index) => (
                        <span key={index} className={`${part.highlight ? 'text-primary font-bold' : ''}`}>
                          {part.text}
                        </span>
                      ))}
                    </div>
                  );
                }}
              />
              }
            </>
            :
            <>
              <TextField
                fullWidth
                variant="outlined"
                name={'store_info/cta/cta_vertical_mask_custom_link_title_2'}
                label="Title"
                value={
                  vertical['store_info/cta/cta_vertical_mask_custom_link_title_2'] !== null
                    ? vertical['store_info/cta/cta_vertical_mask_custom_link_title_2']
                    : ''
                }
                onChange={handleChange}
                className="mb-5"
                inputProps={{
                  shrink: 'true'
                }}
                error={isCustomLinkTitleError2}
                helperText={
                  isCustomLinkTitleError2 &&
                  errors.vertical['store_info/cta/cta_vertical_mask_custom_link_title_2']
                }
              />
              <TextField
                fullWidth
                variant="outlined"
                name={'store_info/cta/cta_vertical_mask_custom_link_2'}
                label="Link"
                value={
                  vertical['store_info/cta/cta_vertical_mask_custom_link_2'] !== null
                    ? vertical['store_info/cta/cta_vertical_mask_custom_link_2']
                    : ''
                }
                onChange={handleChange}
                inputProps={{
                  shrink: 'true'
                }}
                error={isCustomLinkError2}
                helperText={
                  isCustomLinkError2 &&
                  errors.vertical['store_info/cta/cta_vertical_mask_custom_link_2']
                }
              />
            </>
          }
        </Grid>
        <Grid item xs={12} sm={2} className="my-2 ml-4">Link 3</Grid>
        <Grid item xs={12} sm={9}>
          <RadioGroup
            row
            aria-label={'select option 3'}
            name={'store_info/cta/is_cta_vertical_mask_category_3'}
            value={vertical['store_info/cta/is_cta_vertical_mask_category_3']}
            onChange={handleChange}
            className="mb-2"
          >
            <FormControlLabel
              value="1"
              control={<Radio size="small" color="primary" />}
              label="Category"
            />
            <FormControlLabel
              value="0"
              control={<Radio size="small" color="primary" />}
              label="Custom"
            />
          </RadioGroup>
          {vertical['store_info/cta/is_cta_vertical_mask_category_3'] == 1 ?
            <>
              {loadingCategories && <LinearProgress className="mt-4" />}
              {!loadingCategories && errorCategories && <div>Can&apos;t fetch categories data</div>}
              {!loadingCategories && !errorCategories && storeActiveCategoriesWithActiveNavLevel.length &&
              <Autocomplete
                size="small"
                fullWidth
                options={storeActiveCategoriesWithActiveNavLevel}
                getOptionLabel={(option) => option.nameWithPath || ''}
                value={storeActiveCategoriesWithActiveNavLevel.find((category) => (
                  category.id == vertical['store_info/cta/cta_vertical_mask_categoryId_3']) || null
                )}
                getOptionSelected={(option) => (
                  vertical['store_info/cta/cta_vertical_mask_categoryId_3'] == option.id
                )}
                onChange={(_, value, action) => (
                  handleSelectCategory(
                    value,
                    action,
                    'vertical',
                    'store_info/cta/cta_vertical_mask_categoryId_3'
                  )
                )}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    variant="outlined"
                    label="Category"
                  />
                )}
                renderOption={(option, { inputValue }) => {
                  const matches = match(option.nameWithPath, inputValue);
                  const parts = parse(option.nameWithPath, matches);
                  return (
                    <div>
                      {parts.map((part, index) => (
                        <span key={index} className={`${part.highlight ? 'text-primary font-bold' : ''}`}>
                          {part.text}
                        </span>
                      ))}
                    </div>
                  );
                }}
              />
              }
            </>
            :
            <>
              <TextField
                fullWidth
                variant="outlined"
                name={'store_info/cta/cta_vertical_mask_custom_link_title_3'}
                label="Title"
                value={
                  vertical['store_info/cta/cta_vertical_mask_custom_link_title_3'] !== null
                    ? vertical['store_info/cta/cta_vertical_mask_custom_link_title_3']
                    : ''
                }
                onChange={handleChange}
                className="mb-5"
                inputProps={{
                  shrink: 'true'
                }}
                error={isCustomLinkTitleError3}
                helperText={
                  isCustomLinkTitleError3 &&
                  errors.vertical['store_info/cta/cta_vertical_mask_custom_link_title_3']
                }
              />
              <TextField
                fullWidth
                variant="outlined"
                name={'store_info/cta/cta_vertical_mask_custom_link_3'}
                label="Link"
                value={
                  vertical['store_info/cta/cta_vertical_mask_custom_link_3'] !== null
                    ? vertical['store_info/cta/cta_vertical_mask_custom_link_3']
                    : ''
                }
                onChange={handleChange}
                inputProps={{
                  shrink: 'true'
                }}
                error={isCustomLinkError3}
                helperText={
                  isCustomLinkError3 &&
                  errors.vertical['store_info/cta/cta_vertical_mask_custom_link_3']
                }
              />
            </>
          }
        </Grid>
        <Grid item xs={12} sm={2} className="my-2 ml-4">Link 4</Grid>
        <Grid item xs={12} sm={9}>
          <RadioGroup
            row
            aria-label={'select option 4'}
            name={'store_info/cta/is_cta_vertical_mask_category_4'}
            value={vertical['store_info/cta/is_cta_vertical_mask_category_4']}
            onChange={handleChange}
            className="mb-2"
          >
            <FormControlLabel
              value="1"
              control={<Radio size="small" color="primary" />}
              label="Category"
            />
            <FormControlLabel
              value="0"
              control={<Radio size="small" color="primary" />}
              label="Custom"
            />
          </RadioGroup>
          {vertical['store_info/cta/is_cta_vertical_mask_category_4'] == 1 ?
            <>
              {loadingCategories && <LinearProgress className="mt-4" />}
              {!loadingCategories && errorCategories && <div>Can&apos;t fetch categories data</div>}
              {!loadingCategories && !errorCategories && storeActiveCategoriesWithActiveNavLevel.length &&
              <Autocomplete
                size="small"
                fullWidth
                options={storeActiveCategoriesWithActiveNavLevel}
                getOptionLabel={(option) => option.nameWithPath || ''}
                value={storeActiveCategoriesWithActiveNavLevel.find((category) => (
                  category.id == vertical['store_info/cta/cta_vertical_mask_categoryId_4']) || null
                )}
                getOptionSelected={(option) => (
                  vertical['store_info/cta/cta_vertical_mask_categoryId_4'] == option.id
                )}
                onChange={(_, value, action) => (
                  handleSelectCategory(
                    value,
                    action,
                    'vertical',
                    'store_info/cta/cta_vertical_mask_categoryId_4'
                  )
                )}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    variant="outlined"
                    label="Category"
                  />
                )}
                renderOption={(option, { inputValue }) => {
                  const matches = match(option.nameWithPath, inputValue);
                  const parts = parse(option.nameWithPath, matches);
                  return (
                    <div>
                      {parts.map((part, index) => (
                        <span key={index} className={`${part.highlight ? 'text-primary font-bold' : ''}`}>
                          {part.text}
                        </span>
                      ))}
                    </div>
                  );
                }}
              />
              }
            </>
            :
            <>
              <TextField
                fullWidth
                variant="outlined"
                name={'store_info/cta/cta_vertical_mask_custom_link_title_4'}
                label="Title"
                value={
                  vertical['store_info/cta/cta_vertical_mask_custom_link_title_4'] !== null
                    ? vertical['store_info/cta/cta_vertical_mask_custom_link_title_4']
                    : ''
                }
                onChange={handleChange}
                className="mb-5"
                inputProps={{
                  shrink: 'true'
                }}
                error={isCustomLinkTitleError4}
                helperText={
                  isCustomLinkTitleError4 &&
                  errors.vertical['store_info/cta/cta_vertical_mask_custom_link_title_4']
                }
              />
              <TextField
                fullWidth
                variant="outlined"
                name={'store_info/cta/cta_vertical_mask_custom_link_4'}
                label="Link"
                value={
                  vertical['store_info/cta/cta_vertical_mask_custom_link_4'] !== null
                    ? vertical['store_info/cta/cta_vertical_mask_custom_link_4']
                    : ''
                }
                onChange={handleChange}
                inputProps={{
                  shrink: 'true'
                }}
                error={isCustomLinkError4}
                helperText={
                  isCustomLinkError4 &&
                  errors.vertical['store_info/cta/cta_vertical_mask_custom_link_4']
                }
              />
            </>
          }
        </Grid>
        <Grid item xs={12} sm={2} className="my-2 ml-4">Link 5</Grid>
        <Grid item xs={12} sm={9}>
          <RadioGroup
            row
            aria-label={'select option 5'}
            name={'store_info/cta/is_cta_vertical_mask_category_5'}
            value={vertical['store_info/cta/is_cta_vertical_mask_category_5']}
            onChange={handleChange}
            className="mb-2"
          >
            <FormControlLabel
              value="1"
              control={<Radio size="small" color="primary" />}
              label="Category"
            />
            <FormControlLabel
              value="0"
              control={<Radio size="small" color="primary" />}
              label="Custom"
            />
          </RadioGroup>
          {vertical['store_info/cta/is_cta_vertical_mask_category_5'] == 1 ?
            <>
              {loadingCategories && <LinearProgress className="mt-4" />}
              {!loadingCategories && errorCategories && <div>Can&apos;t fetch categories data</div>}
              {!loadingCategories && !errorCategories && storeActiveCategoriesWithActiveNavLevel.length &&
              <Autocomplete
                size="small"
                fullWidth
                options={storeActiveCategoriesWithActiveNavLevel}
                getOptionLabel={(option) => option.nameWithPath || ''}
                value={storeActiveCategoriesWithActiveNavLevel.find((category) => (
                  category.id == vertical['store_info/cta/cta_vertical_mask_categoryId_5']) || null
                )}
                getOptionSelected={(option) => (
                  vertical['store_info/cta/cta_vertical_mask_categoryId_5'] == option.id
                )}
                onChange={(_, value, action) => (
                  handleSelectCategory(
                    value,
                    action,
                    'vertical',
                    'store_info/cta/cta_vertical_mask_categoryId_5'
                  )
                )}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    variant="outlined"
                    label="Category"
                  />
                )}
                renderOption={(option, { inputValue }) => {
                  const matches = match(option.nameWithPath, inputValue);
                  const parts = parse(option.nameWithPath, matches);
                  return (
                    <div>
                      {parts.map((part, index) => (
                        <span key={index} className={`${part.highlight ? 'text-primary font-bold' : ''}`}>
                          {part.text}
                        </span>
                      ))}
                    </div>
                  );
                }}
              />
              }
            </>
            :
            <>
              <TextField
                fullWidth
                variant="outlined"
                name={'store_info/cta/cta_vertical_mask_custom_link_title_5'}
                label="Title"
                value={
                  vertical['store_info/cta/cta_vertical_mask_custom_link_title_5'] !== null
                    ? vertical['store_info/cta/cta_vertical_mask_custom_link_title_5']
                    : ''
                }
                onChange={handleChange}
                className="mb-5"
                inputProps={{
                  shrink: 'true'
                }}
                error={isCustomLinkTitleError5}
                helperText={
                  isCustomLinkTitleError5 &&
                  errors.vertical['store_info/cta/cta_vertical_mask_custom_link_title_5']
                }
              />
              <TextField
                fullWidth
                variant="outlined"
                name={'store_info/cta/cta_vertical_mask_custom_link_5'}
                label="Link"
                value={
                  vertical['store_info/cta/cta_vertical_mask_custom_link_5'] !== null
                    ? vertical['store_info/cta/cta_vertical_mask_custom_link_5']
                    : ''
                }
                onChange={handleChange}
                inputProps={{
                  shrink: 'true'
                }}
                error={isCustomLinkError5}
                helperText={
                  isCustomLinkError5 &&
                  errors.vertical['store_info/cta/cta_vertical_mask_custom_link_5']
                }
              />
            </>
          }
        </Grid>
        <Grid item xs={12} sm={2} className="my-2 ml-4">Link 6</Grid>
        <Grid item xs={12} sm={9}>
          <RadioGroup
            row
            aria-label={'select option 6'}
            name={'store_info/cta/is_cta_vertical_mask_category_6'}
            value={vertical['store_info/cta/is_cta_vertical_mask_category_6']}
            onChange={handleChange}
            className="mb-2"
          >
            <FormControlLabel
              value="1"
              control={<Radio size="small" color="primary" />}
              label="Category"
            />
            <FormControlLabel
              value="0"
              control={<Radio size="small" color="primary" />}
              label="Custom"
            />
          </RadioGroup>
          {vertical['store_info/cta/is_cta_vertical_mask_category_6'] == 1 ?
            <>
              {loadingCategories && <LinearProgress className="mt-4" />}
              {!loadingCategories && errorCategories && <div>Can&apos;t fetch categories data</div>}
              {!loadingCategories && !errorCategories && storeActiveCategoriesWithActiveNavLevel.length &&
              <Autocomplete
                size="small"
                fullWidth
                options={storeActiveCategoriesWithActiveNavLevel}
                getOptionLabel={(option) => option.nameWithPath || ''}
                value={storeActiveCategoriesWithActiveNavLevel.find((category) => (
                  category.id == vertical['store_info/cta/cta_vertical_mask_categoryId_6']) || null
                )}
                getOptionSelected={(option) => (
                  vertical['store_info/cta/cta_vertical_mask_categoryId_6'] == option.id
                )}
                onChange={(_, value, action) => (
                  handleSelectCategory(
                    value,
                    action,
                    'vertical',
                    'store_info/cta/cta_vertical_mask_categoryId_6'
                  )
                )}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    variant="outlined"
                    label="Category"
                  />
                )}
                renderOption={(option, { inputValue }) => {
                  const matches = match(option.nameWithPath, inputValue);
                  const parts = parse(option.nameWithPath, matches);
                  return (
                    <div>
                      {parts.map((part, index) => (
                        <span key={index} className={`${part.highlight ? 'text-primary font-bold' : ''}`}>
                          {part.text}
                        </span>
                      ))}
                    </div>
                  );
                }}
              />
              }
            </>
            :
            <>
              <TextField
                fullWidth
                variant="outlined"
                name={'store_info/cta/cta_vertical_mask_custom_link_title_6'}
                label="Title"
                value={
                  vertical['store_info/cta/cta_vertical_mask_custom_link_title_6'] !== null
                    ? vertical['store_info/cta/cta_vertical_mask_custom_link_title_6']
                    : ''
                }
                onChange={handleChange}
                className="mb-5"
                inputProps={{
                  shrink: 'true'
                }}
                error={isCustomLinkTitleError6}
                helperText={
                  isCustomLinkTitleError6 &&
                  errors.vertical['store_info/cta/cta_vertical_mask_custom_link_title_6']
                }
              />
              <TextField
                fullWidth
                variant="outlined"
                name={'store_info/cta/cta_vertical_mask_custom_link_6'}
                label="Link"
                value={
                  vertical['store_info/cta/cta_vertical_mask_custom_link_6'] !== null
                    ? vertical['store_info/cta/cta_vertical_mask_custom_link_6']
                    : ''
                }
                onChange={handleChange}
                inputProps={{
                  shrink: 'true'
                }}
                error={isCustomLinkError6}
                helperText={
                  isCustomLinkError6 &&
                  errors.vertical['store_info/cta/cta_vertical_mask_custom_link_6']
                }
              />
            </>
          }
        </Grid>
        <Grid item xs={12} sm={2} className="my-2 ml-4">Block Button Category</Grid>
        <Grid item xs={12} sm={9}>
          {loadingCategories && <LinearProgress className="mt-4" />}
          {!loadingCategories && errorCategories && <div>Can&apos;t fetch categories data</div>}
          {!loadingCategories && !errorCategories && storeActiveCategoriesWithActiveNavLevel.length &&
            <Autocomplete
              size="small"
              fullWidth
              options={storeActiveCategoriesWithActiveNavLevel}
              getOptionLabel={(option) => option.nameWithPath || ''}
              value={storeActiveCategoriesWithActiveNavLevel.find((category) => (
                category.id == vertical['store_info/cta/cta_vertical_mask_button_categoryId']) || null
              )}
              getOptionSelected={(option) => (
                vertical['store_info/cta/cta_vertical_mask_button_categoryId'] == option.id
              )}
              onChange={(_, value, action) => (
                handleSelectCategory(
                  value,
                  action,
                  'vertical',
                  'store_info/cta/cta_vertical_mask_button_categoryId'
                )
              )}
              renderInput={(params) => (
                <TextField
                  {...params}
                  variant="outlined"
                  label="Category"
                />
              )}
              renderOption={(option, { inputValue }) => {
                const matches = match(option.nameWithPath, inputValue);
                const parts = parse(option.nameWithPath, matches);
                return (
                  <div>
                    {parts.map((part, index) => (
                      <span key={index} className={`${part.highlight ? 'text-primary font-bold' : ''}`}>
                        {part.text}
                      </span>
                    ))}
                  </div>
                );
              }}
            />
          }
        </Grid>
        <Grid item xs={12} sm={2} className="my-2 ml-4">Block Image</Grid>
        <Grid item xs={12} sm={9}>
          <div>
            <Button
              className="w-full max-w-300 p-0"
              variant="outlined"
              color="primary"
              size="large"
            >
              <label htmlFor="upload-vertical-image" className="flex items-center w-full justify-center px-5 py-2">
                <Icon className="mr-2">upload</Icon>
                Upload Own Image
              </label>
            </Button>
            <input
              className="hidden"
              onChange={handleUpLoadImage}
              onClick={(event) => event.target.value = null}
              id="upload-vertical-image"
              type="file"
              accept="image/png, image/jpeg"
            />
          </div>
          <FormHelperText className="mt-2">
            <div>Files supported: png, jpeg, jpg. File size should be less than 250kB.</div>
            <div>Image resolution should be exactly 380x552px.</div>
          </FormHelperText>
          <Button
            className="w-full max-w-300 mt-2 mr-2"
            variant="outlined"
            color="primary"
            size="large"
            onClick={() => toggleOpenImageDialog(true)}
          >
            <Icon className="mr-2">wallpaper</Icon>
            Select Image From Library
          </Button>
          <Button
            className="w-full max-w-300 mt-2 mr-2 bg-periwinkle-gray"
            variant="outlined"
            color="primary"
            size="large"
            onClick={() => toggleOpenOwnImageDialog(true)}
          >
            <Icon className="mr-2">wallpaper</Icon>
            Select From Own Images
          </Button>
        </Grid>
      </Grid>
      <Divider className="my-8" />
      {verticalCTATagData.id && openImageDialog &&
        <ImageGalleryDialog
          title="Select Vertical Block CTA Image From Library"
          open={openImageDialog}
          setOpen={toggleOpenImageDialog}
          onImageSelect={handleSelectImage}
          imageTagId={verticalCTATagData.id}
          cols={4}
          rowHeight={340}
        />
      }
      {(ownImagesTagIds && openOwnImageDialog && assetImagesData[ownImagesTagIds]?.items?.length) ?
        <ImageGalleryDialog
          title="Select Vertical Block CTA Image From Own Images"
          open={openOwnImageDialog}
          setOpen={toggleOpenOwnImageDialog}
          onImageSelect={handleSelectImage}
          imageTagId={ownImagesTagIds}
          isPrivate
          cols={4}
          rowHeight={340}
        />
        :
        <Snackbar
          anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
          autoHideDuration={3000}
          onClose={() => toggleOpenOwnImageDialog(false)}
          open={openOwnImageDialog}
        >
          <MuiAlert elevation={6} variant="filled" severity="warning">
            You have no your own uploaded images.
            Firstly, you have to upload at least one image.
          </MuiAlert>
        </Snackbar>
      }
    </div>
  );
};

export default CTAVertical;
